/**
 * Conversion tracking for the marketing site. Everything goes through the GTM
 * dataLayer, so GA4 and Google Ads tags are configured in the container, not here.
 *
 * Events pushed:
 * - `cta_click`       every click on a conversion link, with `cta_type`
 *                     (start_trial | book_demo | login | contact_sales | support),
 *                     `cta_text`, `cta_location` (the slice or region it sat in),
 *                     `link_url` and `page_path`.
 * - `form_submit`     a contact or demo form was submitted successfully, with `form_name`.
 * - `view_page`       fired on every client-side navigation with `page_type`
 *                     (pricing | case_study | product | industry | blog | home | page),
 *                     so GTM can fire pricing-visited and case-study-viewed without
 *                     relying on the initial page_view alone.
 *
 * Attribution: utm_* and click ids from the landing URL are kept in localStorage
 * for 90 days and appended to every outgoing signital.app link, so the app can
 * store them on the account at signup and revenue can be tied back to the campaign.
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
  }
}

export type CtaType = 'start_trial' | 'book_demo' | 'login' | 'contact_sales' | 'support'

export const APP_ORIGIN = 'https://signital.app'
const ATTRIBUTION_KEY = 'signital_attribution'
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000
const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'fbclid',
]

export function track(event: string, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, ...params })
}

export function ctaTypeOf(href: string): CtaType | null {
  if (href.startsWith(`${APP_ORIGIN}/register`)) return 'start_trial'
  if (href.startsWith(`${APP_ORIGIN}/login`)) return 'login'
  if (href.includes('calendly.com/')) return 'book_demo'
  if (href.startsWith('mailto:sales@')) return 'contact_sales'
  if (href.startsWith('mailto:support@') || href.startsWith('https://docs.signital.com')) return 'support'
  return null
}

export function pageTypeOf(pathname: string): string {
  if (pathname.startsWith('/lp/')) return 'landing'
  if (pathname === '/' || pathname === '/homepage') return 'home'
  if (pathname === '/pricing') return 'pricing'
  if (pathname.startsWith('/solutions/case-studies/')) return 'case_study'
  if (pathname.startsWith('/solutions/industries/')) return 'industry'
  if (pathname.startsWith('/product/')) return 'product'
  if (pathname.startsWith('/blog')) return 'blog'
  if (pathname === '/contact-us') return 'contact'
  return 'page'
}

type Attribution = {
  params: Record<string, string>
  landing_page: string
  referrer: string
  saved_at: number
}

function readAttribution(): Attribution | null {
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Attribution
    if (!parsed.saved_at || Date.now() - parsed.saved_at > ATTRIBUTION_TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

/** Saves campaign parameters from the current URL. A new campaign click replaces an old one. */
export function captureAttribution() {
  try {
    const url = new URL(window.location.href)
    const params: Record<string, string> = {}
    for (const key of ATTRIBUTION_PARAMS) {
      const v = url.searchParams.get(key)
      if (v) params[key] = v.slice(0, 200)
    }
    if (Object.keys(params).length === 0) {
      if (!readAttribution() && document.referrer) {
        // No campaign parameters, but remember where the first visit came from.
        const ref = new URL(document.referrer)
        if (ref.hostname !== url.hostname) {
          window.localStorage.setItem(
            ATTRIBUTION_KEY,
            JSON.stringify({
              params: { utm_source: ref.hostname, utm_medium: 'referral' },
              landing_page: url.pathname,
              referrer: document.referrer,
              saved_at: Date.now(),
            } satisfies Attribution)
          )
        }
      }
      return
    }
    window.localStorage.setItem(
      ATTRIBUTION_KEY,
      JSON.stringify({
        params,
        landing_page: url.pathname,
        referrer: document.referrer,
        saved_at: Date.now(),
      } satisfies Attribution)
    )
  } catch {
    // Storage blocked; the click still gets tracked, just without stored attribution.
  }
}

/** Appends stored campaign parameters to a signital.app link, without overriding any it already has. */
export function withAttribution(href: string): string {
  if (!href.startsWith(APP_ORIGIN)) return href
  const stored = readAttribution()
  if (!stored) return href
  try {
    const url = new URL(href)
    for (const [k, v] of Object.entries(stored.params)) {
      if (!url.searchParams.has(k)) url.searchParams.set(k, v)
    }
    if (!url.searchParams.has('landing_page') && stored.landing_page) {
      url.searchParams.set('landing_page', stored.landing_page)
    }
    return url.toString()
  } catch {
    return href
  }
}
